import { useState } from "react";
import { Plus } from "lucide-react";
import { RoomGrid } from "../components/RoomGrid";
import { RoomNameDialog } from "../components/RoomNameDialog";
import { FloatingAddButton } from "../components/FloatingAddButton";
import { SUGGESTED_ROOMS } from "../services/app/constants";
import { formatIssueCount } from "../services/app/reportStatus";
import { trackRoomCreated, trackRoomDeleted } from "../services/analytics/analyticsEvents";
import { addRoom, deleteRoom, renameRoom } from "../services/db/reportRepo";
import type { Issue, Report, Room } from "../services/db/schema";

interface RoomsRouteProps {
  report: Report;
  rooms: Room[];
  issues: Issue[];
  onNavigate: (path: string) => void;
  onChanged: () => void;
}

type DialogState = { mode: "add" } | { mode: "rename"; room: Room } | undefined;

export function RoomsRoute({
  report,
  rooms,
  issues,
  onNavigate,
  onChanged
}: RoomsRouteProps): JSX.Element {
  const [dialog, setDialog] = useState<DialogState>();

  async function createRoom(name: string) {
    const room = await addRoom(report.id, name);
    trackRoomCreated({ roomCount: rooms.length + 1, conditionCount: issues.length });
    onChanged();
    onNavigate(`/rooms/${room.id}`);
  }

  async function removeRoom(room: Room) {
    const count = issues.filter((issue) => issue.roomId === room.id).length;
    if (!window.confirm(`Delete ${room.name}${count ? ` and ${formatIssueCount(count)}` : ""} from this draft?`)) {
      return;
    }
    await deleteRoom(room.id);
    trackRoomDeleted({ roomCount: Math.max(0, rooms.length - 1), conditionCount: Math.max(0, issues.length - count) });
    onChanged();
  }

  return (
    <section className="screen">
      <div className="topbar">
        <div className="topbar__title">
          <h1>Rooms</h1>
          <p>
            {formatIssueCount(issues.length)} in this record.
          </p>
        </div>
      </div>

      {rooms.length === 0 ? (
        <EmptyRooms
          suggestions={SUGGESTED_ROOMS}
          onAddCustom={() => setDialog({ mode: "add" })}
          onAddSuggested={(name) => void createRoom(name)}
        />
      ) : (
        <RoomGrid
          rooms={rooms}
          issues={issues}
          onOpenRoom={(roomId) => onNavigate(`/rooms/${roomId}`)}
          onRenameRoom={(room) => setDialog({ mode: "rename", room })}
          onDeleteRoom={(room) => void removeRoom(room)}
        />
      )}

      {rooms.length > 0 && <FloatingAddButton label="Add room" onClick={() => setDialog({ mode: "add" })} />}

      {dialog?.mode === "add" && (
        <RoomNameDialog
          title="Add room"
          onCancel={() => setDialog(undefined)}
          onSubmit={async (name) => {
            setDialog(undefined);
            await createRoom(name);
          }}
        />
      )}
      {dialog?.mode === "rename" && (
        <RoomNameDialog
          title="Rename room"
          initialValue={dialog.room.name}
          onCancel={() => setDialog(undefined)}
          onSubmit={async (name) => {
            await renameRoom(dialog.room.id, name);
            setDialog(undefined);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

function EmptyRooms({
  suggestions,
  onAddCustom,
  onAddSuggested
}: {
  suggestions: readonly string[];
  onAddCustom: () => void;
  onAddSuggested: (name: string) => void;
}): JSX.Element {
  return (
    <div className="empty-state">
      <div className="empty-state__mark" aria-hidden="true">
        <Plus size={28} />
      </div>
      <h2>Add your first room</h2>
      <p>Start with the room you are standing in, then add condition notes as you find them.</p>
      <button className="button button--primary" type="button" onClick={onAddCustom}>
        <Plus size={18} aria-hidden="true" />
        Add room
      </button>
      <div className="suggestion-row" aria-label="Suggested rooms">
        {suggestions.map((name) => (
          <button className="suggestion-chip" type="button" key={name} onClick={() => onAddSuggested(name)}>
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}
